import { ISelectItem } from "@components/inputs/select/Select.types";
import { getDaysUnit, getWeeksUnit, IFieldInfoProperties, TTemporalDialogSettings } from "@components/smart/FieldInfo";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    PrPayrollSettingEntity,
    PrPayrollSettingTemporalCurrentEntity
} from "@odata/GeneratedEntityTypes";
import { PrHolidayPayoutTypeCode, PrHolidayShiftAssignmentCode } from "@odata/GeneratedEnums";
import {
    getTemporalAdditionalProperties,
    getTemporalPropertyFieldDefinition,
    TemporalValidityFieldsDef
} from "@odata/TemporalUtils";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { timeStampToMinutesFormatter, timeStampToMinutesParser } from "@pages/payroll/payslip/Payslip.def";
import { BaseWorkingHoursPerWeekDef } from "@pages/payroll/workingPatterns/WorkingPatterns.utils";
import { TextStyled } from "@pages/recurringTasks/RecurringTask.styles";
import { IAppContext } from "contexts/appContext/AppContext.types";
import { BasicInputSizes, CacheStrategy, FieldType, GroupedField, LabelStatus, ValidatorType } from "enums";
import i18next from "i18next";
import React from "react";

import { TValue } from "../../../global.types";
import { IFormDef } from "../../../views/formView/Form";


export function getSickDaysDef(temporalDialogSettings: TTemporalDialogSettings): Partial<IFieldInfoProperties> {
    return {
        type: FieldType.NumberInput,
        width: BasicInputSizes.S,
        useForValidation: true,
        fieldSettings: {
            unit: getDaysUnit,
            temporalDialog: {
                ...temporalDialogSettings,
                granularity: "month",
                withoutGaps: true,
                cannotBeEnded: true
            },
            formatter: (val: TValue): string => (val ?? 0).toString(),
            min: 0,
        },
        validator: {
            type: ValidatorType.Number,
            settings: {
                min: 0
            }
        }
    };
}

export function getVacationWeeksDef(temporalDialogSettings: TTemporalDialogSettings): Partial<IFieldInfoProperties> {
    return {
        type: FieldType.NumberInput,
        width: BasicInputSizes.S,
        useForValidation: true,
        fieldSettings: {
            unit: getWeeksUnit,
            temporalDialog: {
                ...temporalDialogSettings,
                granularity: "month",
                withoutGaps: true,
                cannotBeEnded: true
            },
            formatter: (val: TValue): string => (val ?? 0).toString(),
            min: 0,
        },
        validator: {
            type: ValidatorType.Number,
                settings: {
                min: 0
            }
        },
    };
}

export const getDefinitions: IGetDefinition = (_context: IAppContext): IDefinition => {
    const form: IFormDef = {
        id: `${EntityTypeName.PrPayrollSetting}Form`,
        translationFiles: getDefinitions.translationFiles,
        fieldDefinition: {
            // we don't want new entity TemporalPropertyBag to be null => use default value
            [PrPayrollSettingEntity.TemporalPropertyBag]: {
                defaultValue: []
            },
            // todo: temporary hidden, we don't support automatic period close right now
            // [PrPayrollSettingEntity.IsPaidOutAutomatically]: {
            //     type: FieldType.Switch,
            //     label: i18next.t("Payroll:Setting.IsPaidOutAutomatically"),
            //     defaultValue: true,
            //     affectedFields: [
            //         { id: PrPayrollSettingEntity.PayDay },
            //         { id: PrPayrollSettingEntity.SubstitutePayDay },
            //     ],
            //     customizationData: {
            //         dependents: [PrPayrollSettingEntity.PayDay, PrPayrollSettingEntity.SubstitutePayDay]
            //     }
            // },
            [PrPayrollSettingEntity.PayDay]: {
                type: FieldType.ComboBox,
                label: i18next.t("Payroll:Setting.PayDay"),
                width: BasicInputSizes.S,
                cacheStrategy: CacheStrategy.EndOfTime,
                fieldSettings: {
                    itemsFactory: async (): Promise<ISelectItem[]> => {
                        const days: ISelectItem[] = [];
                        for (let i = 1; i < 29; i++) {
                            days.push({
                                id: i.toString(),
                                label: i + "."
                            });
                        }
                        return days;
                    }
                },
            },
            [PrPayrollSettingEntity.SubstitutePayDay]: {
                type: FieldType.ComboBox,
                labelStatus: LabelStatus.Hidden,
                width: BasicInputSizes.L,
                groupedField: GroupedField.MultiEnd,
                extraFieldContent: (
                    <TextStyled>{i18next.t("Payroll:Setting.PayDaySubstituteDesc")}</TextStyled>
                ),
                fieldSettings: {
                    displayName: "Name"
                }
            },
            ...getTemporalPropertyFieldDefinition({
                propName: PrPayrollSettingTemporalCurrentEntity.SickDays,
                definition: {
                    ...getSickDaysDef({
                        dialogTitle: i18next.t("Payroll:Setting.SickDaysTemporalDialog"),
                        columns: [PrPayrollSettingTemporalCurrentEntity.SickDays],
                    }),
                    defaultValue: 0
                },
                currentDefinition: {
                    isRequired: false
                }
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrPayrollSettingTemporalCurrentEntity.VacationWeeks,
                definition: {
                    ...getVacationWeeksDef({
                        dialogTitle: i18next.t("Payroll:Setting.VacationWeeksTemporalDialog"),
                        columns: [PrPayrollSettingTemporalCurrentEntity.VacationWeeks],
                    }),
                    defaultValue: 4,
                },
                currentDefinition: {
                    isRequired: false
                }
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrPayrollSettingTemporalCurrentEntity.WorkingHoursPerWeek,
                definition: {
                    ...BaseWorkingHoursPerWeekDef,
                    label: i18next.t("Payroll:Setting.FullTimeHoursPerWeek"),
                    defaultValue: 40,
                    width: BasicInputSizes.M,
                    fieldSettings: {
                        ...BaseWorkingHoursPerWeekDef.fieldSettings,
                        temporalDialog: {
                            dialogTitle: i18next.t("Payroll:Setting.ChangeFullTimeValue"),
                            columns: [PrPayrollSettingTemporalCurrentEntity.WorkingHoursPerWeek],
                            granularity: "month",
                            withoutGaps: true,
                            cannotBeEnded: true
                        },
                    }
                }
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrPayrollSettingTemporalCurrentEntity.StatutoryInsuranceCompanyRate,
                definition: {
                    type: FieldType.NumberInput,
                    width: BasicInputSizes.S,
                    label: i18next.t("Payroll:Setting.StatutoryInsuranceCompanyRate"),
                    defaultValue: 5.6,
                    isRequired: true,
                    fieldSettings: {
                        unit: "‰",
                        min: 0,
                        max: 1000,
                        temporalDialog: {
                            dialogTitle: i18next.t("Payroll:Setting.StatutoryInsuranceCompanyRateTemporalDialog"),
                            columns: [PrPayrollSettingTemporalCurrentEntity.StatutoryInsuranceCompanyRate],
                            granularity: "quarter",
                            withoutGaps: true,
                            cannotBeEnded: true
                        },
                    },
                    validator: {
                        type: ValidatorType.Number,
                        settings: {
                            min: 0,
                            max: 1000
                        }
                    }
                }
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrPayrollSettingTemporalCurrentEntity.HolidayPayoutType,
                definition: {
                    type: FieldType.ComboBox,
                    width: BasicInputSizes.L,
                    label: i18next.t("Payroll:Setting.HolidayPayoutCalculation"),
                    defaultValue: PrHolidayPayoutTypeCode.IncludeInSalary,
                    isRequired: true,
                    fieldSettings: {
                        displayName: "Name",
                        temporalDialog: {
                            dialogTitle: i18next.t("Payroll:Setting.HolidayPayoutCalculation"),
                            columns: [PrPayrollSettingTemporalCurrentEntity.HolidayPayoutType],
                            granularity: "month",
                            withoutGaps: true,
                            cannotBeEnded: true
                        },
                    }
                }
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrPayrollSettingTemporalCurrentEntity.MinimalMinutesForMealVoucher,
                definition: {
                    type: FieldType.NumberInput,
                    label: i18next.t("Payroll:Setting.HoursCount"),
                    width: BasicInputSizes.S,
                    isRequired: true,
                    fieldSettings: {
                        showSteppers: true,
                        temporalDialog: {
                            dialogTitle: i18next.t("Payroll:Setting.MinimalMinutesForMealVoucher"),
                            columns: [PrPayrollSettingTemporalCurrentEntity.MinimalMinutesForMealVoucher],
                            granularity: "month",
                            withoutGaps: true,
                            cannotBeEnded: true
                        },
                    },
                    parser: timeStampToMinutesParser,
                    formatter: timeStampToMinutesFormatter
                }
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrPayrollSettingTemporalCurrentEntity.HolidayShiftAssignment,
                definition: {
                    type: FieldType.ComboBox,
                    width: BasicInputSizes.L,
                    label: i18next.t("Payroll:Setting.HolidayShiftAssignment"),
                    defaultValue: PrHolidayShiftAssignmentCode.SplitByMidnight,
                    isRequired: true,
                    fieldSettings: {
                        displayName: "Name",
                        temporalDialog: {
                            dialogTitle: i18next.t("Payroll:Setting.HolidayShiftAssignment"),
                            columns: [PrPayrollSettingTemporalCurrentEntity.HolidayShiftAssignment],
                            granularity: "month",
                            withoutGaps: true,
                            cannotBeEnded: true
                        },
                    }
                }
            }),
            ...TemporalValidityFieldsDef
        },
        groups: [
            {
                id: "payrollDates",
                title: i18next.t("Payroll:Setting.Payroll"),
                rows: [[
                    { id: PrPayrollSettingEntity.PayDay },
                    { id: PrPayrollSettingEntity.SubstitutePayDay },
                    // { id: PrPayrollSettingEntity.IsPaidOutAutomatically },
                ]]
            },
            {
                id: "statutoryInsuranceCompany",
                title: i18next.t("Payroll:Setting.StatutoryInsuranceCompany"),
                rows: [[
                    { id: createPath(PrPayrollSettingEntity.CurrentTemporalPropertyBag, PrPayrollSettingTemporalCurrentEntity.StatutoryInsuranceCompanyRate) },
                ]]
            },
            {
                id: "workingPatternDefaults",
                title: i18next.t("Payroll:Setting.WorkingPatternDefaults"),
                rows: [[
                    { id: createPath(PrPayrollSettingEntity.CurrentTemporalPropertyBag, PrPayrollSettingTemporalCurrentEntity.WorkingHoursPerWeek) },
                ]]
            },
            {
                id: "defaultAbsences",
                title: i18next.t("Payroll:Setting.DefaultAbsences"),
                rows: [[
                    { id: createPath(PrPayrollSettingEntity.CurrentTemporalPropertyBag, PrPayrollSettingTemporalCurrentEntity.VacationWeeks) },
                    { id: createPath(PrPayrollSettingEntity.CurrentTemporalPropertyBag, PrPayrollSettingTemporalCurrentEntity.SickDays) },
                ]]
            }, {
                id: "holidayPayout",
                title: i18next.t("Payroll:Setting.HolidayPayoutCalculationGroupTitle"),
                rows: [[
                    { id: createPath(PrPayrollSettingEntity.CurrentTemporalPropertyBag, PrPayrollSettingTemporalCurrentEntity.HolidayPayoutType) },
                    { id: createPath(PrPayrollSettingEntity.CurrentTemporalPropertyBag, PrPayrollSettingTemporalCurrentEntity.HolidayShiftAssignment) },
                ]]
            }, {
                id: "minimalMinutesForMealVoucher",
                title: i18next.t("Payroll:Setting.MinimalMinutesForMealVoucher"),
                rows: [[
                    { id: createPath(PrPayrollSettingEntity.CurrentTemporalPropertyBag, PrPayrollSettingTemporalCurrentEntity.MinimalMinutesForMealVoucher) },
                ]]
            }
        ],
        additionalProperties: [
            ...getTemporalAdditionalProperties(PrPayrollSettingTemporalCurrentEntity, PrPayrollSettingEntity)
        ]
    };

    return {
        entitySet: EntitySetName.PrPayrollSettings,
        form
    };
};

getDefinitions.translationFiles = ["Payroll", "Common", "Error"];
setDefByEntityType(EntityTypeName.PrPayrollSetting, getDefinitions);
